import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

function RosePetal({ id, x, onDone }) {
  return (
    <motion.div
      className="fixed top-0 z-[10000] pointer-events-none"
      style={{ left: `${x}%` }}
      initial={{ y: -60, opacity: 1, rotate: 0, scale: 0.8 }}
      animate={{ y: '105vh', opacity: [1, 1, 0.5, 0], rotate: Math.random() > 0.5 ? 360 : -360, scale: [0.8, 1.1, 0.9, 0.7] }}
      transition={{ duration: 2.5 + Math.random() * 1.5, ease: 'easeIn' }}
      onAnimationComplete={onDone}
    >
      <svg width="32" height="48" viewBox="0 0 32 48" fill="none" stroke="hsl(265,85%,70%)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
        {/* stem */}
        <path d="M16 28 Q14 36 13 44" />
        {/* left leaf */}
        <path d="M14 36 Q8 32 9 26 Q13 30 14 36z" />
        {/* drooping petals (🥀 wilted style) */}
        <path d="M16 10 C16 10 12 7 10 10 C8 13 11 17 16 17" />
        <path d="M16 10 C16 10 20 7 22 10 C24 13 21 17 16 17" />
        <path d="M16 10 C16 10 13 5 16 3 C19 5 16 10 16 10" />
        {/* drooping outer petals */}
        <path d="M16 17 C14 20 10 20 9 17 C8 14 11 12 14 14" />
        <path d="M16 17 C18 20 22 20 23 17 C24 14 21 12 18 14" />
        <path d="M16 17 C15 22 13 24 12 22 C11 20 13 18 16 17" />
        <path d="M16 17 C17 22 19 24 20 22 C21 20 19 18 16 17" />
        {/* center */}
        <circle cx="16" cy="13" r="2" />
      </svg>
    </motion.div>
  );
}

export default function MascotCharacter() {
  const [isDancing, setIsDancing] = useState(false);
  const [showLetter, setShowLetter] = useState(false);
  const [envelopeOpen, setEnvelopeOpen] = useState(false);
  const [roses, setRoses] = useState([]);

  useEffect(() => {
    if (!envelopeOpen) return;
    const spawn = () => {
      setRoses(prev => [
        ...prev,
        ...Array.from({ length: 14 }, (_, i) => ({ id: Date.now() + i, x: 2 + Math.random() * 96 }))
      ]);
    };
    spawn();
    const t1 = setTimeout(spawn, 600);
    const t2 = setTimeout(spawn, 1200);
    const t3 = setTimeout(spawn, 1800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [envelopeOpen]);

  const removeRose = (id) => setRoses(prev => prev.filter(r => r.id !== id));

  const handleClick = () => {
    if (isDancing) return;
    setIsDancing(true);
    setTimeout(() => setIsDancing(false), 1200);
    setRoses([]);
    setEnvelopeOpen(false);
    setShowLetter(true);
    setTimeout(() => setEnvelopeOpen(true), 400);
  };

  return (
    <div className="flex flex-col items-center gap-1 select-none">
      <AnimatePresence>
        {roses.map(r => <RosePetal key={r.id} id={r.id} x={r.x} onDone={() => removeRose(r.id)} />)}
      </AnimatePresence>

      {/* Letter Modal */}
      <AnimatePresence>
        {showLetter && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setShowLetter(false)}
          >
            <motion.div
              initial={{ scale: 0.6, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.6, y: 40 }}
              transition={{ type: 'spring', damping: 18 }}
              onClick={e => e.stopPropagation()}
              className="relative w-80"
            >
              {/* Envelope body */}
              <div className="w-full h-20 bg-primary/10 border border-primary/30 rounded-b-2xl flex items-end justify-center pb-2">
                <div className="w-3/4 h-px bg-primary/20" />
              </div>

              {/* Envelope flap */}
              <motion.div
                initial={{ rotateX: 0 }}
                animate={{ rotateX: envelopeOpen ? -160 : 0 }}
                transition={{ delay: 0.1, duration: 0.6, ease: 'easeInOut' }}
                style={{ transformOrigin: 'top center', transformStyle: 'preserve-3d', perspective: 800 }}
                className="absolute top-0 left-0 right-0 h-20 z-10 overflow-hidden"
              >
                <svg viewBox="0 0 320 80" className="w-full h-full" preserveAspectRatio="none">
                  <polygon points="0,0 320,0 160,80" fill="hsl(var(--primary) / 0.25)" stroke="hsl(var(--primary) / 0.4)" strokeWidth="1" />
                </svg>
              </motion.div>

              {/* Letter sliding out */}
              <AnimatePresence>
                {envelopeOpen && (
                  <motion.div
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: -140, opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.55, ease: 'easeOut' }}
                    className="absolute left-3 right-3 bottom-2 bg-card border border-primary/30 rounded-xl px-6 py-5 shadow-2xl shadow-primary/20 z-20"
                  >
                    <p className="font-mono-cv text-[9px] text-primary/40 tracking-widest text-center mb-4 uppercase">— a note for you —</p>
                    <p className="text-foreground/80 text-sm leading-relaxed mb-5 text-center" style={{ fontFamily: 'Syne, sans-serif', fontStyle: 'normal', lineHeight: '1.8' }}>
                      thank you for visiting my website...
                    </p>
                    <div className="border-t border-primary/10 pt-3 text-right">
                      <p className="text-primary/50 text-xs font-mono-cv">with love,</p>
                      <p className="text-primary text-base font-bold" style={{ fontFamily: 'Dancing Script, cursive', display: 'inline-block', transform: 'rotate(-6deg)', transformOrigin: 'right center' }}>mbk</p>
                    </div>
                    <button
                      onClick={() => setShowLetter(false)}
                      className="mt-4 w-full font-mono-cv text-[10px] text-muted-foreground hover:text-primary transition-colors"
                    >
                      close ✕
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Character */}
      <motion.div
        onClick={handleClick}
        className="cursor-pointer"
        animate={isDancing ? {
          rotate: [-15, 15, -15, 15, -10, 10, 0],
          y: [-8, -16, -8, -16, -8, -4, 0],
          scale: [1, 1.15, 1, 1.15, 1, 1.05, 1],
        } : {
          y: [0, -6, 0],
          rotate: [-3, 3, -3],
        }}
        transition={isDancing ? {
          duration: 1.2,
          ease: 'easeInOut',
        } : {
          duration: 2.4,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        whileHover={{ scale: 1.1 }}
      >
        <img
          src="https://media.base44.com/images/public/69f0e204b691b63890319b5a/3d538749b_generated_image.png"
          alt="mbk mascot"
          width={80}
          height={100}
          style={{ imageRendering: 'auto', display: 'block', mixBlendMode: 'screen' }}
          className="drop-shadow-[0_0_12px_rgba(139,92,246,0.5)]"
        />
      </motion.div>

      {/* Tap hint */}
      <p className="font-mono-cv text-[9px] text-primary/30 tracking-wide">clock me</p>
    </div>
  );
}